import {
  NO_LENS_VISUALIZER_LENS_KEY,
  type VisualizerConversation,
  type VisualizerLens,
} from "./uploadedConversationsVisualizerDataset"
import { resolveFailureIssueDots } from "./uploadedConversationsVisualizerFailureDots"
import {
  clampPointToRadius,
  insetOverlayAnchor,
  resolveChaosAnchor,
  type PlatePoint,
} from "./uploadedConversationsVisualizerOverlayDotGeometry"
import {
  hashString32,
  mixHash32,
  unitFloatFromSeed,
} from "./uploadedConversationsVisualizerOverlayHash"
import {
  buildBackgroundParticles,
  clampNumber,
  clampToPlate,
  PLATE_DOMAIN_RADIUS,
} from "./uploadedConversationsVisualizerParticles"
import { type SetSlice } from "./UploadedConversationsVisualizerPieBackground"

export interface OverlayDotBlueprint {
  amp: number
  anchorU: number
  anchorV: number
  bucketLabel: string
  chaosU: number
  chaosV: number
  conversation: VisualizerConversation
  dotId: string
  frequencyA: number
  frequencyB: number
  nodalStrength: number
  phaseA: number
  phaseB: number
  sliceIndex: number
}

const GOLDEN_ANGLE_RADIANS = Math.PI * (3 - Math.sqrt(5))
const ANOMALY_DISTRIBUTION_POOL_SCALE = 10
const CLUSTER_OVERLAY_ANCHOR_INSET_SCALE = 0.92
const ANOMALY_OVERLAY_ANCHOR_INSET_SCALE = 0.985
const BUCKET_SEED_LAYOUT_MAX_RADIUS = PLATE_DOMAIN_RADIUS * 0.92
const BUCKET_SEED_LAYOUT_PULL_TO_BASE_SHARE = 0.18
const BUCKET_SEED_LAYOUT_PUSH_SHARE = 0.5
const CLUSTER_WANDER_RADIUS_MAX = 0.16
const CLUSTER_WANDER_RECENT_POINT_WINDOW = 10
const ANOMALY_DISTRIBUTION_SLICES = [
  { startAngle: -Math.PI / 2, span: Math.PI * 2 },
]

interface BucketEntry {
  bucketKey: string
  bucketLabel: string
  conversations: VisualizerConversation[]
  seedPosition?: { x: number; y: number }
  sliceIndex: number
}

function resolveBucketSeedLayout(
  bucketEntries: BucketEntry[],
  visualizationHash: number,
) {
  const maxBucketSize = Math.max(
    ...bucketEntries.map((bucketEntry) => bucketEntry.conversations.length),
    1,
  )
  const sliceCount = Math.max(bucketEntries.length, 1)
  const baseRotation =
    unitFloatFromSeed(visualizationHash, 0x9e3779b9) * Math.PI * 2
  const bucketSeeds = Array.from({ length: sliceCount }, (_, sliceIndex) => {
    const bucketEntry = bucketEntries[sliceIndex]
    const bucketSize = Math.max(1, bucketEntry?.conversations.length ?? 0)
    const sizeWeight = Math.sqrt(bucketSize / maxBucketSize)
    const configuredSeed = bucketEntry?.seedPosition
      ? clampPointToRadius(
          clampToPlate(bucketEntry.seedPosition.x, bucketEntry.seedPosition.y),
          BUCKET_SEED_LAYOUT_MAX_RADIUS,
        )
      : null
    const seedHash = mixHash32(
      visualizationHash ^ Math.imul(sliceIndex + 1, 0x45d9f3b),
    )
    const radius =
      Math.sqrt(unitFloatFromSeed(seedHash, 0xa53c7d19)) *
      BUCKET_SEED_LAYOUT_MAX_RADIUS *
      (1.12 - sizeWeight * 0.48)
    const theta =
      baseRotation + unitFloatFromSeed(seedHash, 0x3c6ef372) * Math.PI * 2
    const baseSeed = clampToPlate(
      0.5 + Math.cos(theta) * radius,
      0.5 + Math.sin(theta) * radius,
    )

    return {
      baseSeed: configuredSeed ?? baseSeed,
      footprintRadius: clampNumber(0.07 + sizeWeight * 0.11, 0.07, 0.18),
      isConfigured: configuredSeed !== null,
      seed: configuredSeed ?? baseSeed,
      sizeWeight,
    }
  })

  if (bucketSeeds.length <= 1) {
    return bucketSeeds.map((entry) => entry.seed)
  }

  const relaxationIterations = Math.max(
    4,
    Math.min(12, Math.round(7 + Math.sqrt(sliceCount) * 0.8)),
  )

  for (let iteration = 0; iteration < relaxationIterations; iteration += 1) {
    for (let leftIndex = 0; leftIndex < bucketSeeds.length; leftIndex += 1) {
      for (
        let rightIndex = leftIndex + 1;
        rightIndex < bucketSeeds.length;
        rightIndex += 1
      ) {
        const left = bucketSeeds[leftIndex]
        const right = bucketSeeds[rightIndex]
        const deltaU = right.seed.u - left.seed.u
        const deltaV = right.seed.v - left.seed.v
        const distance = Math.hypot(deltaU, deltaV)
        const preferredSeparation = clampNumber(
          (left.footprintRadius + right.footprintRadius) * 1.08,
          0.1,
          0.34,
        )
        if (distance >= preferredSeparation) {
          continue
        }

        const overlap = preferredSeparation - distance
        const fallbackTheta =
          (iteration + leftIndex * 2 + rightIndex * 3 + 1) *
            GOLDEN_ANGLE_RADIANS +
          baseRotation
        const unitU =
          distance > 1e-6 ? deltaU / distance : Math.cos(fallbackTheta)
        const unitV =
          distance > 1e-6 ? deltaV / distance : Math.sin(fallbackTheta)
        const pushDistance = overlap * 0.5 * BUCKET_SEED_LAYOUT_PUSH_SHARE

        left.seed = clampPointToRadius(
          clampToPlate(
            left.seed.u - unitU * pushDistance,
            left.seed.v - unitV * pushDistance,
          ),
          BUCKET_SEED_LAYOUT_MAX_RADIUS,
        )
        right.seed = clampPointToRadius(
          clampToPlate(
            right.seed.u + unitU * pushDistance,
            right.seed.v + unitV * pushDistance,
          ),
          BUCKET_SEED_LAYOUT_MAX_RADIUS,
        )
      }
    }

    for (const bucketSeed of bucketSeeds) {
      const sizeAdjustedPullShare =
        BUCKET_SEED_LAYOUT_PULL_TO_BASE_SHARE *
        (1 - bucketSeed.sizeWeight * 0.22)
      const pullToBaseShare = bucketSeed.isConfigured
        ? sizeAdjustedPullShare * 1.35
        : sizeAdjustedPullShare
      bucketSeed.seed = clampPointToRadius(
        clampToPlate(
          bucketSeed.seed.u +
            (bucketSeed.baseSeed.u - bucketSeed.seed.u) * pullToBaseShare,
          bucketSeed.seed.v +
            (bucketSeed.baseSeed.v - bucketSeed.seed.v) * pullToBaseShare,
        ),
        BUCKET_SEED_LAYOUT_MAX_RADIUS,
      )
    }
  }

  return bucketSeeds.map((entry) => entry.seed)
}

function resolveBucketSeed(sliceIndex: number, bucketSeedLayout: PlatePoint[]) {
  return bucketSeedLayout[sliceIndex] ?? clampToPlate(0.5, 0.5)
}

function resolveSeededClusterPoint(
  bucketSeed: PlatePoint,
  priorWanderSeed: PlatePoint,
  priorClusterPoints: PlatePoint[],
  conversationIndex: number,
  bucketSize: number,
  maxBucketSize: number,
  hash: number,
) {
  const clusterSize = Math.max(1, bucketSize)
  const sizeWeight = Math.sqrt(clusterSize / Math.max(1, maxBucketSize))
  const wanderRadius = clampNumber(
    0.024 + sizeWeight * 0.104 + Math.sqrt(clusterSize) * 0.0036,
    0.03,
    CLUSTER_WANDER_RADIUS_MAX,
  )
  const steppedSeedIndex = Math.floor(conversationIndex / 4)
  const steppedSeedTheta =
    steppedSeedIndex * GOLDEN_ANGLE_RADIANS +
    unitFloatFromSeed(hash, 0xbeef1234)
  const steppedSeedRadius =
    Math.min(
      wanderRadius * 0.86,
      0.012 + steppedSeedIndex * (0.0013 + sizeWeight * 0.0005),
    ) *
    (0.76 + unitFloatFromSeed(hash, 0x11335577) * 0.42)
  const steppedSeed = clampPointToRadius(
    clampToPlate(
      bucketSeed.u + Math.cos(steppedSeedTheta) * steppedSeedRadius,
      bucketSeed.v + Math.sin(steppedSeedTheta) * steppedSeedRadius,
    ),
    wanderRadius,
    bucketSeed,
  )
  const driftTheta = unitFloatFromSeed(hash, 0x1f123bb5) * Math.PI * 2
  const driftDistance =
    clampNumber(wanderRadius * 0.3, 0.005, 0.02) *
    (0.55 + unitFloatFromSeed(hash, 0x5bf03635) * 0.95)
  const driftedSeed = clampToPlate(
    priorWanderSeed.u + Math.cos(driftTheta) * driftDistance,
    priorWanderSeed.v + Math.sin(driftTheta) * driftDistance,
  )
  const recenterShare = 0.18 + unitFloatFromSeed(hash, 0xc2b2ae35) * 0.17
  const nextWanderSeed = clampPointToRadius(
    clampToPlate(
      driftedSeed.u + (steppedSeed.u - driftedSeed.u) * recenterShare,
      driftedSeed.v + (steppedSeed.v - driftedSeed.v) * recenterShare,
    ),
    wanderRadius,
    bucketSeed,
  )

  const localTheta = unitFloatFromSeed(hash, 0x9e3779b1) * Math.PI * 2
  const localRadius =
    (0.003 + unitFloatFromSeed(hash, 0x85ebca6b) * 0.0165) *
    clampNumber(0.78 + sizeWeight * 1.3, 0.78, 2.08)
  let point = clampToPlate(
    nextWanderSeed.u + Math.cos(localTheta) * localRadius,
    nextWanderSeed.v + Math.sin(localTheta) * localRadius,
  )

  const separationRadius = clampNumber(
    0.0036 + sizeWeight * 0.007 + Math.sqrt(clusterSize) * 0.00055,
    0.0036,
    0.017,
  )
  const priorStartIndex = Math.max(
    0,
    priorClusterPoints.length - CLUSTER_WANDER_RECENT_POINT_WINDOW,
  )
  for (
    let priorIndex = priorStartIndex;
    priorIndex < priorClusterPoints.length;
    priorIndex += 1
  ) {
    const priorPoint = priorClusterPoints[priorIndex]
    const deltaU = point.u - priorPoint.u
    const deltaV = point.v - priorPoint.v
    const distance = Math.hypot(deltaU, deltaV)
    if (distance >= separationRadius) {
      continue
    }

    const fallbackTheta =
      (conversationIndex + priorIndex + 1) * GOLDEN_ANGLE_RADIANS +
      unitFloatFromSeed(hash, 0x27d4eb2f) * Math.PI * 2
    const unitU = distance > 1e-6 ? deltaU / distance : Math.cos(fallbackTheta)
    const unitV = distance > 1e-6 ? deltaV / distance : Math.sin(fallbackTheta)
    const pushDistance = (separationRadius - distance) * 0.55
    point = clampToPlate(
      point.u + unitU * pushDistance,
      point.v + unitV * pushDistance,
    )
  }

  point = clampPointToRadius(point, wanderRadius * 1.2, bucketSeed)

  return {
    point,
    wanderSeed: nextWanderSeed,
  }
}

function resolveBucketEntries(slices: SetSlice[], lens: VisualizerLens) {
  const bucketAssignments = new Map(
    lens.bucketAssignments.map((bucketAssignment) => [
      bucketAssignment.bucketKey,
      bucketAssignment,
    ]),
  )

  return slices.map((slice, sliceIndex): BucketEntry => {
    const bucketAssignment = bucketAssignments.get(slice.bucket.key)
    return {
      bucketKey: slice.bucket.key,
      bucketLabel: bucketAssignment?.label ?? slice.bucket.label,
      conversations: bucketAssignment?.conversations ?? [],
      seedPosition: bucketAssignment?.seedPosition,
      sliceIndex,
    }
  })
}

function resolveSeededClusterDots(
  slices: SetSlice[],
  lens: VisualizerLens,
  visualizationKey: string,
) {
  const visualizationHash = hashString32(visualizationKey)
  const bucketEntries = resolveBucketEntries(slices, lens)
  const maxBucketSize = Math.max(
    ...bucketEntries.map((bucketEntry) => bucketEntry.conversations.length),
    1,
  )
  const bucketSeedLayout = resolveBucketSeedLayout(
    bucketEntries,
    visualizationHash,
  )

  return bucketEntries.flatMap((bucketEntry) => {
    const bucketSeed = resolveBucketSeed(
      bucketEntry.sliceIndex,
      bucketSeedLayout,
    )
    const orderedConversations = [...bucketEntry.conversations].sort(
      (left, right) => {
        const leftHash = hashString32(
          `${visualizationKey}:${bucketEntry.bucketKey}:${left.id}`,
        )
        const rightHash = hashString32(
          `${visualizationKey}:${bucketEntry.bucketKey}:${right.id}`,
        )
        if (leftHash !== rightHash) {
          return leftHash - rightHash
        }
        return left.id.localeCompare(right.id)
      },
    )
    const clusterPoints: PlatePoint[] = []
    let wanderSeed = bucketSeed

    return orderedConversations.map((conversation, conversationIndex) => {
      const hash = hashString32(
        `${visualizationKey}:${bucketEntry.bucketKey}:${conversation.id}`,
      )
      const seededClusterPoint = resolveSeededClusterPoint(
        bucketSeed,
        wanderSeed,
        clusterPoints,
        conversationIndex,
        orderedConversations.length,
        maxBucketSize,
        hash,
      )
      wanderSeed = seededClusterPoint.wanderSeed
      clusterPoints.push(seededClusterPoint.point)
      const anchor = insetOverlayAnchor(
        seededClusterPoint.point,
        CLUSTER_OVERLAY_ANCHOR_INSET_SCALE,
      )
      const amp = 0.58 + ((hash >>> 7) % 100) / 160
      const chaosAnchor = resolveChaosAnchor(anchor.u, anchor.v, amp, hash)

      return {
        amp,
        anchorU: anchor.u,
        anchorV: anchor.v,
        bucketLabel: bucketEntry.bucketLabel,
        chaosU: chaosAnchor.u,
        chaosV: chaosAnchor.v,
        conversation,
        dotId: conversation.dotId ?? conversation.id,
        frequencyA: 0.8 + ((hash >>> 10) % 130) / 100,
        frequencyB: 0.9 + ((hash >>> 16) % 130) / 100,
        nodalStrength: clampNumber(
          0.72 - conversationIndex * 0.004,
          0.32,
          0.74,
        ),
        phaseA: ((hash >>> 2) % 6280) / 1000,
        phaseB: ((hash >>> 12) % 6280) / 1000,
        sliceIndex: bucketEntry.sliceIndex,
      } satisfies OverlayDotBlueprint
    })
  })
}

function resolveDistributedAnomalyDots(
  slices: SetSlice[],
  lens: VisualizerLens,
  visualizationKey: string,
) {
  const bucketEntries = resolveBucketEntries(slices, lens)
  const allConversations = bucketEntries.flatMap((bucketEntry) =>
    bucketEntry.conversations.map((conversation) => ({
      bucketLabel: bucketEntry.bucketLabel,
      conversation,
      sliceIndex: bucketEntry.sliceIndex,
    })),
  )
  const orderedConversations = [...allConversations].sort((left, right) => {
    const leftHash = hashString32(`${visualizationKey}:${left.conversation.id}`)
    const rightHash = hashString32(
      `${visualizationKey}:${right.conversation.id}`,
    )
    if (leftHash !== rightHash) {
      return leftHash - rightHash
    }
    return left.conversation.id.localeCompare(right.conversation.id)
  })
  const anomalyParticlePool = buildBackgroundParticles(
    Math.max(
      orderedConversations.length,
      orderedConversations.length * ANOMALY_DISTRIBUTION_POOL_SCALE,
    ),
    ANOMALY_DISTRIBUTION_SLICES,
    lens.preset,
  )
  const poolCount = anomalyParticlePool.length
  const usedPoolIndices = new Set<number>()

  return orderedConversations.map((entry, conversationIndex) => {
    const hash = hashString32(`${visualizationKey}:${entry.conversation.id}`)
    const seedBase = mixHash32(
      hash ^ Math.imul(conversationIndex + 1, 0x9e3779b9),
    )
    const startIndex = poolCount > 0 ? seedBase % poolCount : 0
    let stride =
      poolCount > 1 ? 1 + (mixHash32(hash ^ 0x7f4a7c15) % (poolCount - 1)) : 1
    if (poolCount % 2 === 0 && stride % 2 === 0) {
      stride = (stride + 1) % poolCount || 1
    }
    let chosenIndex = startIndex
    for (let attempt = 0; attempt < poolCount; attempt += 1) {
      const candidateIndex = (startIndex + attempt * stride) % poolCount
      if (usedPoolIndices.has(candidateIndex)) {
        continue
      }
      chosenIndex = candidateIndex
      break
    }
    usedPoolIndices.add(chosenIndex)
    const distributedParticle = anomalyParticlePool[chosenIndex]
    const anchor = insetOverlayAnchor(
      clampToPlate(
        distributedParticle?.anchorU ?? 0.5,
        distributedParticle?.anchorV ?? 0.5,
      ),
      ANOMALY_OVERLAY_ANCHOR_INSET_SCALE,
    )
    const amp = 0.58 + ((hash >>> 7) % 100) / 160
    const chaosAnchor = resolveChaosAnchor(anchor.u, anchor.v, amp, hash)

    return {
      amp,
      anchorU: anchor.u,
      anchorV: anchor.v,
      bucketLabel: entry.bucketLabel,
      chaosU: chaosAnchor.u,
      chaosV: chaosAnchor.v,
      conversation: entry.conversation,
      dotId: entry.conversation.dotId ?? entry.conversation.id,
      frequencyA: 0.8 + ((hash >>> 10) % 130) / 100,
      frequencyB: 0.9 + ((hash >>> 16) % 130) / 100,
      nodalStrength: 0.42 + ((hash >>> 20) % 100) / 400,
      phaseA: ((hash >>> 2) % 6280) / 1000,
      phaseB: ((hash >>> 12) % 6280) / 1000,
      sliceIndex: entry.sliceIndex,
    } satisfies OverlayDotBlueprint
  })
}

export function resolveOverlayDotBlueprints(
  slices: SetSlice[],
  lens: VisualizerLens,
  visualizationKey: string,
) {
  if (lens.key === "failures") {
    return resolveFailureIssueDots(lens, visualizationKey)
  }
  if (lens.key === "anomalies" || lens.key === NO_LENS_VISUALIZER_LENS_KEY) {
    return resolveDistributedAnomalyDots(slices, lens, visualizationKey)
  }

  return resolveSeededClusterDots(slices, lens, visualizationKey)
}
